import { isFunction } from 'lodash-es';
import { ReactElement, ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';

/**
 * localStorage key that controls whether the new React 18 `createRoot` engine
 * is used for the main app and Mercator. (The connect apps opt out entirely by
 * passing `force18 = false` and never consult this key.)
 *
 * The engine has to be chosen before React mounts, which is too early for the
 * LaunchDarkly React provider / hooks. So instead of reading an LD flag here, we
 * read a plain localStorage value synchronously at startup. Two things write to
 * this key:
 *   1. A manual override for local/QA testing (set it by hand in devtools).
 *   2. The app mirrors the LD flag into this key once flags are available (see
 *      `setRender18Preference`). Setting the flag to `false` therefore disables
 *      React 18 on the user's next page load — no code change or redeploy
 *      required (one refresh lag).
 *
 * React 18 is the default: the engine is used unless the key is explicitly
 * `'false'`. This means a fresh load (empty localStorage) — including every
 * Cypress test — renders with React 18 without any seeding.
 */
const RENDER18_STORAGE_KEY = 'mastery:render18';

/**
 * Hard kill switch (ME-413745) for the React 18 `createRoot` engine.
 *
 * Flip to `true` to force the legacy React 17 `ReactDOM.render` engine
 * EVERYWHERE — this overrides the LaunchDarkly flag, the `mastery:render18`
 * localStorage value, AND any `force18` argument. Its purpose is to let us ship
 * the accumulated R18-compatibility fixes to production while still rendering on
 * the stable R17 engine, so the fixes can bake before we actually switch the
 * engine on. Intended usage: keep `false` while developing/testing the fixes
 * (so R18 stays active and CI exercises it), then set to `true` in the LAST
 * commit before merging. A follow-up PR flips it back to `false` to re-enable
 * flag-driven R18 rollout.
 */
const FORCE_LEGACY_REACT_17 = true;

const isRender18Enabled = (): boolean => {
  try {
    // Default on: only the explicit string 'false' disables React 18.
    return localStorage.getItem(RENDER18_STORAGE_KEY) !== 'false';
  } catch {
    // localStorage can throw in locked-down/embedded contexts. Default to React 18.
    return true;
  }
};

/**
 * Mirror the current LD flag value into localStorage so the next page load can
 * pick the right rendering engine synchronously. Call this from inside React
 * once the LD flag value is known.
 */
export const setRender18Preference = (enabled: boolean): void => {
  try {
    const next = String(enabled);
    // TEMP: while both engines coexist, log when the mirrored flag
    // value actually changes so we can confirm the LD hook updated localStorage
    // live. Remove once the React 18 engine is the permanent default.
    const previous = localStorage.getItem(RENDER18_STORAGE_KEY);
    if (previous !== next) {
      // eslint-disable-next-line no-console
      console.log(
        // eslint-disable-next-line lingui/no-unlocalized-strings
        `[render18] flag changed: '${RENDER18_STORAGE_KEY}' ${previous} → ${next}. Refresh to apply the new engine.`,
      );
      localStorage.setItem(RENDER18_STORAGE_KEY, next);
    }
  } catch {
    // Ignore: failing to persist just means we keep the current engine next load.
  }
};

/**
 * Render the root element of the application.
 *
 * - Omit `force18` (main app / Mercator): React 18 is used unless the LD flag
 *   has been explicitly set to `false` (see `isRender18Enabled`).
 * - Pass `force18 = false` (connect apps): always use the legacy React 17
 *   engine and ignore the flag.
 * - Pass `force18 = true`: always use React 18, ignoring the flag.
 */
export const renderRoot = (node: ReactElement, force18?: boolean): void => {
  const rootEl = document.getElementById('root');
  if (!rootEl) {
    // eslint-disable-next-line lingui/no-unlocalized-strings
    throw new Error(
      'renderRoot: could not find the #root element to mount to.',
    );
  }
  const useReact18 = FORCE_LEGACY_REACT_17
    ? false
    : (force18 ?? isRender18Enabled());
  // TEMP (ME-413745): log the active rendering engine on every page load while
  // both engines coexist, so we can confirm which engine any given page used.
  // Remove once the React 18 engine is the permanent default.
  // eslint-disable-next-line no-console
  console.log(
    // eslint-disable-next-line lingui/no-unlocalized-strings
    `[render18] rendering with ${
      useReact18 ? 'React 18 createRoot()' : 'legacy React 17 ReactDOM.render()'
    }`,
  );
  if (useReact18) {
    // Render with React 18 if the app requests it, or if the mirrored LD flag is on.
    const root = createRoot(rootEl);
    root.render(node);
  } else {
    // Render with React 17.
    ReactDOM.render(node, rootEl);
  }
};

/** Renders a label. React 18+ no longer allows rendering a function directly. Must evaluate before passing to JSX. */
export const renderLabel = (
  label?: string | ((item?: Maybe<fixMe>) => ReactNode),
): ReactNode => {
  return isFunction(label) ? label() : (label ?? '');
};

/** Renders a title. React 18+ no longer allows rendering a function directly. Must evaluate before passing to JSX. */
export const renderTitle = (
  title?: ReactNode | (() => ReactNode),
): ReactNode => {
  return isFunction(title) ? title() : (title ?? '');
};
